import React, { useState, useEffect, useRef } from 'react';
import { axiosInstance, API } from '../App';
import { toast } from 'sonner';
import { Plus, Trash2, X, Edit2, Download, FileText, Upload, Eye, Calendar, Building2, Briefcase } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { convertArabic } from 'arabic-reshaper';
import { NotoSansArabicBase64 } from '../fonts/NotoSansArabic';

const processArabicText = (text) => {
  if (!text) return '';
  const arabicRegex = /[\u0600-\u06FF]/;
  if (!arabicRegex.test(text)) return text;
  return convertArabic(text);
};

const Contracts = () => {
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [selectedContract, setSelectedContract] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);
  const formFileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    customer_id: '',
    contract_title: '',
    manual_contract_no: '',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    contract_value: 0,
    payment_terms: 'net_30',
    custom_payment_terms: '',
    description: '',
    terms_conditions: '',
    notes: ''
  });

  const paymentTermsOptions = [
    { value: 'immediate', label: 'Immediate - فوري' },
    { value: 'net_15', label: 'Net 15 Days' },
    { value: 'net_30', label: 'Net 30 Days' },
    { value: 'net_60', label: 'Net 60 Days' },
    { value: 'net_90', label: 'Net 90 Days' },
    { value: 'custom', label: 'Custom - مخصص' }
  ];

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    { value: 'active', label: 'Active', color: 'bg-green-100 text-green-700' },
    { value: 'completed', label: 'Completed', color: 'bg-blue-100 text-blue-700' },
    { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
    { value: 'expired', label: 'Expired', color: 'bg-yellow-100 text-yellow-700' }
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [contractsRes, customersRes] = await Promise.all([
        axiosInstance.get('/contracts'),
        axiosInstance.get('/customers')
      ]);
      setContracts(contractsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id || !formData.contract_title) {
      toast.error('Please fill required fields');
      return;
    }

    try {
      let contractId;
      if (editingContract) {
        await axiosInstance.put(`/contracts/${editingContract.id}`, formData);
        contractId = editingContract.id;
        toast.success('Contract updated successfully');
      } else {
        const response = await axiosInstance.post('/contracts', formData);
        contractId = response.data.id;
        if (response.data.approval_status === 'pending') {
          toast.success('Contract submitted for approval');
        } else {
          toast.success('Contract created successfully');
        }
      }
      
      // Upload pending file if exists
      if (pendingFile && contractId) {
        const uploadFormData = new FormData();
        uploadFormData.append('file', pendingFile);
        try {
          await axiosInstance.post(`/contracts/${contractId}/upload-attachment`, uploadFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          toast.success('Attachment uploaded');
        } catch (uploadError) {
          console.error('Failed to upload attachment:', uploadError);
        }
      }
      
      setShowModal(false);
      resetForm();
      setPendingFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contract');
    }
  };

  const handleEdit = (contract) => {
    setEditingContract(contract);
    setFormData({
      customer_id: contract.customer_id,
      contract_title: contract.contract_title,
      manual_contract_no: contract.manual_contract_no || '',
      start_date: contract.start_date,
      end_date: contract.end_date,
      contract_value: contract.contract_value,
      payment_terms: contract.payment_terms,
      custom_payment_terms: contract.custom_payment_terms || '',
      description: contract.description || '',
      terms_conditions: contract.terms_conditions || '',
      notes: contract.notes || ''
    });
    setShowModal(true);
  };

  const handleStatusChange = async (contractId, newStatus) => {
    try {
      await axiosInstance.put(`/contracts/${contractId}/status?status=${newStatus}`);
      toast.success('Status updated');
      fetchData();
      if (selectedContract?.id === contractId) {
        setSelectedContract({ ...selectedContract, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUploadAttachment = async (contractId, file) => {
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    try {
      const response = await axiosInstance.post(
        `/contracts/${contractId}/upload-attachment`,
        formDataUpload,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Contract file uploaded');
      
      if (selectedContract && selectedContract.id === contractId) {
        setSelectedContract({ ...selectedContract, attachment_url: response.data.attachment_url });
      }
      fetchData();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contract?')) {
      try {
        await axiosInstance.delete(`/contracts/${id}`);
        toast.success('Contract deleted successfully');
        if (selectedContract?.id === id) {
          setSelectedContract(null);
        }
        fetchData();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete contract');
      }
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      contract_title: '',
      manual_contract_no: '',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      contract_value: 0,
      payment_terms: 'net_30',
      custom_payment_terms: '',
      description: '',
      terms_conditions: '',
      notes: ''
    });
    setEditingContract(null);
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const getStatusColor = (status) => {
    const option = statusOptions.find(s => s.value === status);
    return option?.color || 'bg-gray-100 text-gray-700';
  };

  const downloadPDF = async (contract) => {
    const doc = new jsPDF();
    
    doc.addFileToVFS('NotoSansArabic-Regular.ttf', NotoSansArabicBase64);
    doc.addFont('NotoSansArabic-Regular.ttf', 'NotoSansArabic', 'normal');
    
    // Logo
    try {
      const logoResponse = await fetch('/logo.png');
      if (logoResponse.ok) {
        const logoBlob = await logoResponse.blob();
        const logoBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(logoBlob);
        });
        doc.addImage(logoBase64, 'PNG', 10, 5, 52, 30);
      }
    } catch (err) {}
    
    // Header
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(12);
    doc.setTextColor(10, 66, 117);
    doc.text(processArabicText('شركة الطريق العالي المتميز'), 190, 10, { align: 'right' });
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Distinct High Road', 190, 16, { align: 'right' });
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Hafsa Bint Omar Street, AlRawdah Dist, Riyadh, KSA', 190, 22, { align: 'right' });
    doc.text('VAT: 314477769800003 | CR: 7052942476', 190, 27, { align: 'right' });
    
    doc.setDrawColor(10, 66, 117);
    doc.setLineWidth(0.5);
    doc.line(20, 38, 190, 38);

    // Title
    doc.setFontSize(20);
    doc.setTextColor(10, 66, 117);
    doc.setFont('helvetica', 'bold');
    doc.text('CONTRACT', 105, 50, { align: 'center' });
    
    doc.setFont('NotoSansArabic', 'normal');
    doc.setFontSize(14);
    doc.text(processArabicText('عقد'), 105, 58, { align: 'center' });

    // Contract Info Box
    doc.setFillColor(248, 250, 252);
    doc.roundedRect(10, 65, 190, 45, 3, 3, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(0);
    
    doc.text('Contract No:', 15, 75);
    doc.text('Title:', 15, 83);
    doc.text('Customer:', 15, 91);
    doc.text('Value:', 15, 99);
    
    doc.text('Start Date:', 110, 75);
    doc.text('End Date:', 110, 83);
    doc.text('Payment Terms:', 110, 91);
    doc.text('Status:', 110, 99);
    
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(10, 66, 117);
    doc.text(contract.contract_number, 45, 75);
    doc.text(contract.contract_title, 45, 83);
    doc.text(contract.customer_name || '', 45, 91);
    doc.text(`${contract.contract_value.toLocaleString()} SAR`, 45, 99);
    
    doc.text(contract.start_date, 145, 75);
    doc.text(contract.end_date, 145, 83);
    doc.text(contract.payment_terms.replace('_', ' ').toUpperCase(), 145, 91);
    doc.text(contract.status.toUpperCase(), 145, 99);

    // Description
    if (contract.description) {
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text('Description:', 15, 120);
      doc.setFont('helvetica', 'normal');
      const desc = contract.description;
      if (/[\u0600-\u06FF]/.test(desc)) {
        doc.setFont('NotoSansArabic', 'normal');
        doc.text(processArabicText(desc), 15, 128, { maxWidth: 180 });
      } else {
        doc.text(desc, 15, 128, { maxWidth: 180 });
      }
    }

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('This is a computer-generated document.', 105, 285, { align: 'center' });

    doc.save(`Contract-${contract.contract_number}.pdf`);
    toast.success('PDF downloaded');
  };

  // Stats
  const stats = {
    total: contracts.length,
    active: contracts.filter(c => c.status === 'active').length,
    totalValue: contracts.reduce((sum, c) => sum + c.contract_value, 0),
    totalInvoiced: contracts.reduce((sum, c) => sum + (c.total_invoiced || 0), 0)
  };

  if (loading) {
    return <div className="text-center py-12">Loading contracts...</div>;
  }

  return (
    <div data-testid="contracts-page">
      <div className="page-header">
        <h2 className="page-title">Customer Contracts</h2>
        <button data-testid="add-contract-button" onClick={openNewModal} className="btn-primary">
          <Plus className="w-5 h-5 inline mr-2" />
          New Contract
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="stat-card">
          <div className="stat-icon blue"><FileText className="w-6 h-6" /></div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Contracts</h3>
          <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon green"><Briefcase className="w-6 h-6" /></div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Active</h3>
          <p className="text-3xl font-bold text-green-600">{stats.active}</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon purple"><span className="font-bold text-lg">SAR</span></div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Value</h3>
          <p className="text-2xl font-bold text-purple-600">{stats.totalValue.toLocaleString()} SAR</p>
        </div>
        <div className="stat-card">
          <div className="stat-icon orange"><span className="font-bold text-lg">SAR</span></div>
          <h3 className="text-gray-600 text-sm font-medium mb-1">Total Invoiced</h3>
          <p className="text-2xl font-bold text-orange-600">{stats.totalInvoiced.toLocaleString()} SAR</p>
        </div>
      </div>

      {/* Contracts Table - Split View */}
      <div className="flex gap-4">
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-300 ${selectedContract ? 'w-1/2' : 'w-full'}`}>
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Contract No</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Customer</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Title</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Value</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {contracts.length > 0 ? (
                contracts.map((contract) => (
                  <tr 
                    key={contract.id}
                    data-testid={`contract-row-${contract.id}`}
                    onClick={() => setSelectedContract(selectedContract?.id === contract.id ? null : contract)}
                    className={`cursor-pointer hover:bg-blue-50 transition-colors ${selectedContract?.id === contract.id ? 'bg-blue-100 border-l-4 border-blue-500' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-semibold text-blue-600">{contract.contract_number}</span>
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-900">{contract.customer_name}</p>
                      {contract.customer_name_arabic && (
                        <p className="text-sm text-gray-500" dir="rtl">{contract.customer_name_arabic}</p>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium">{contract.contract_title}</p>
                      {contract.contract_title_arabic && (
                        <p className="text-sm text-gray-500" dir="rtl">{contract.contract_title_arabic}</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold">{contract.contract_value.toLocaleString()} SAR</td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(contract.status)}`}>
                        {contract.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => downloadPDF(contract)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg" title="Download PDF">
                          <Download className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleEdit(contract)} className="p-2 text-green-600 hover:bg-green-50 rounded-lg" title="Edit">
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDelete(contract.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg" title="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-4 py-12 text-center text-gray-500">
                    No contracts found. Create your first contract!
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Contract Details Panel */}
        {selectedContract && (
          <div className="w-1/2 bg-white rounded-xl shadow-lg border border-gray-200 p-6 sticky top-4 max-h-[80vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedContract.contract_number}</h3>
                <p className="text-sm text-gray-500">Contract Details</p>
              </div>
              <button onClick={() => setSelectedContract(null)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status & Actions */}
            <div className="flex items-center gap-3 mb-6">
              <select
                value={selectedContract.status}
                onChange={(e) => handleStatusChange(selectedContract.id, e.target.value)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border-0 cursor-pointer ${getStatusColor(selectedContract.status)}`}
              >
                {statusOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <button onClick={() => downloadPDF(selectedContract)} className="btn-primary text-sm py-1 px-3">
                <Download className="w-4 h-4 inline mr-1" /> PDF
              </button>
            </div>

            {/* Contract Title */}
            <div className="bg-purple-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-purple-800">{selectedContract.contract_title}</h4>
              {selectedContract.contract_title_arabic && (
                <p className="text-purple-600" dir="rtl">{selectedContract.contract_title_arabic}</p>
              )}
            </div>

            {/* Customer Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Building2 className="w-4 h-4" /> Customer
              </h4>
              <p className="font-medium">{selectedContract.customer_name}</p>
              {selectedContract.customer_name_arabic && (
                <p className="text-gray-600" dir="rtl">{selectedContract.customer_name_arabic}</p>
              )}
            </div>

            {/* Contract Info */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-gray-700 mb-2 flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Contract Period
              </h4>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-gray-500">Start Date:</span>
                  <p className="font-medium">{selectedContract.start_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">End Date:</span>
                  <p className="font-medium">{selectedContract.end_date}</p>
                </div>
                <div>
                  <span className="text-gray-500">Payment Terms:</span>
                  <p className="font-medium">{selectedContract.payment_terms.replace('_', ' ')}</p>
                </div>
                <div>
                  <span className="text-gray-500">Projects:</span>
                  <p className="font-medium text-blue-600">{selectedContract.projects_count || 0}</p>
                </div>
              </div>
            </div>

            {/* Financial Summary */}
            <div className="bg-blue-50 rounded-lg p-4 mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Contract Value:</span>
                <span className="font-bold text-blue-600">{selectedContract.contract_value.toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-600">Total Invoiced:</span>
                <span className="font-medium text-green-600">{(selectedContract.total_invoiced || 0).toLocaleString()} SAR</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Paid:</span>
                <span className="font-medium text-purple-600">{(selectedContract.total_paid || 0).toLocaleString()} SAR</span>
              </div>
            </div>

            {/* Description */}
            {selectedContract.description && (
              <div className="mb-4 p-3 bg-yellow-50 rounded-lg">
                <h4 className="font-semibold text-gray-700 mb-1">Description</h4>
                <p className="text-sm text-gray-600">{selectedContract.description}</p>
              </div>
            )}

            {/* Contract File */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-semibold text-gray-700">Contract File</h4>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".pdf,application/pdf,image/*"
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files[0]) {
                      handleUploadAttachment(selectedContract.id, e.target.files[0]);
                    }
                  }}
                />
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? 'Uploading...' : 'Upload File'}
                </button>
              </div>
              
              {selectedContract.attachment_url ? (
                <div className="border rounded-lg p-4 bg-red-50">
                  <div className="flex items-center gap-3">
                    <FileText className="w-12 h-12 text-red-600" />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">Contract Document</p>
                      <p className="text-sm text-gray-500">PDF/Image file</p>
                    </div>
                    <a
                      href={`${API.replace('/api', '')}${selectedContract.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-red-600 text-white px-3 py-2 rounded-lg text-sm flex items-center gap-1 hover:bg-red-700"
                    >
                      <Eye className="w-4 h-4" /> View
                    </a>
                  </div>
                </div>
              ) : (
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-gray-400 mb-2" />
                  <p className="text-gray-500 text-sm">No contract file attached</p>
                  <p className="text-gray-400 text-xs mt-1">Upload PDF or Image</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingContract ? `Edit ${editingContract.contract_number}` : 'New Contract'}
              </h3>
              <button onClick={() => setShowModal(false)} className="btn-icon">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Customer */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Customer *</label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                    className="form-input"
                    required
                  >
                    <option value="">Select Customer</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.name_english || c.name_arabic}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Contract Value (SAR) *</label>
                  <input
                    type="number"
                    value={formData.contract_value}
                    onChange={(e) => setFormData({ ...formData, contract_value: parseFloat(e.target.value) || 0 })}
                    className="form-input"
                    min="0"
                    step="0.01"
                    required
                  />
                </div>
              </div>

              {/* Title */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Contract Name *</label>
                  <input
                    type="text"
                    value={formData.contract_title}
                    onChange={(e) => setFormData({ ...formData, contract_title: e.target.value })}
                    className="form-input"
                    placeholder="Construction Contract"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Contract No</label>
                  <input
                    type="text"
                    value={formData.manual_contract_no}
                    onChange={(e) => setFormData({ ...formData, manual_contract_no: e.target.value })}
                    className="form-input"
                    placeholder="e.g., CTR-2024-001"
                  />
                  <p className="text-xs text-gray-500 mt-1">Optional - Leave empty for auto-generated number</p>
                </div>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="form-group">
                  <label className="form-label">Start Date *</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date *</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Terms</label>
                  <select
                    value={formData.payment_terms}
                    onChange={(e) => setFormData({ ...formData, payment_terms: e.target.value })}
                    className="form-input"
                  >
                    {paymentTermsOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.payment_terms === 'custom' && (
                <div className="form-group mb-4">
                  <label className="form-label">Custom Payment Terms</label>
                  <input
                    type="text"
                    value={formData.custom_payment_terms}
                    onChange={(e) => setFormData({ ...formData, custom_payment_terms: e.target.value })}
                    className="form-input"
                    placeholder="e.g., 50% advance, 50% on completion"
                  />
                </div>
              )}

              {/* Description */}
              <div className="form-group mb-4">
                <label className="form-label">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Contract scope and details..."
                />
              </div>

              {/* Terms & Conditions */}
              <div className="form-group mb-4">
                <label className="form-label">Terms & Conditions</label>
                <textarea
                  value={formData.terms_conditions}
                  onChange={(e) => setFormData({ ...formData, terms_conditions: e.target.value })}
                  className="form-input"
                  rows="3"
                  placeholder="Contract terms and conditions..."
                />
              </div>

              {/* Notes */}
              <div className="form-group mb-4">
                <label className="form-label">Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="form-input"
                  rows="2"
                  placeholder="Additional notes..."
                />
              </div>

              {/* Attachment Upload */}
              <div className="form-group mb-4">
                <label className="form-label flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gray-500" />
                  Contract Attachment (PDF/Image)
                </label>
                <div className="p-4 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <input
                    type="file"
                    ref={formFileInputRef}
                    accept="image/*,.pdf,application/pdf"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files[0]) {
                        setPendingFile(e.target.files[0]);
                      }
                    }}
                  />
                  {pendingFile ? (
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg border">
                      <FileText className="w-8 h-8 text-purple-600" />
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 text-sm">{pendingFile.name}</p>
                        <p className="text-xs text-gray-500">
                          {(pendingFile.size / 1024).toFixed(1)} KB
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPendingFile(null)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div 
                      className="flex flex-col items-center justify-center py-4 cursor-pointer"
                      onClick={() => formFileInputRef.current?.click()}
                    >
                      <Upload className="w-8 h-8 text-gray-400 mb-2" />
                      <p className="text-sm text-gray-500">Click to upload PDF or Image</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button type="submit" className="btn-primary flex-1">
                  {editingContract ? 'Update Contract' : 'Create Contract'}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contracts;
